import { getPinCounterLabel } from './getPinCounterLabel';
import { EIcon, IOffer } from '../../../repositories/dailyrent/entities/offer/Offer';
import { IFeatureProperties } from '../../../types/map';

export interface IPinViewModel {
  /** Цена в пилюле; null — пин не рисуется */
  price: number | null;
  /** Показывать префикс «от»: цена минимальная из нескольких */
  isPriceFrom: boolean;
  isViewed: boolean;
  isFavorite: boolean;
  /** Иконки объявлений в порядке первого появления, без повторов */
  icons: EIcon[];
  counterLabel: string | null;
}

function getUniqueIcons(offers: IOffer[]): EIcon[] {
  const icons = offers.map(offer => offer.icon).filter((icon): icon is EIcon => Boolean(icon));

  return Array.from(new Set(icons));
}

function getMinPrice(offers: IOffer[]): number | null {
  const prices = offers.map(offer => offer.averagePricePerDay).filter((price): price is number => Boolean(price));

  return prices.length > 0 ? Math.min(...prices) : null;
}

/**
 * Данные пилюли пина по всем объявлениям фичи.
 *
 * У одиночного пина правила вырождаются в его единственное объявление,
 * у мультипина — агрегируют весь список (минимальная цена, «хотя бы один»).
 *
 * focusedOffer — наведённое в листинге или открытое в превью объявление:
 * пин показывает его данные вместо агрегата, каунтер при этом остаётся.
 * Принимается объектом, а не id: ховер ищет по realtyId, превью по cianId.
 */
export function getPinViewModel(properties: IFeatureProperties, focusedOffer?: IOffer | null): IPinViewModel {
  const { offers } = properties;
  const counterLabel = getPinCounterLabel(properties);

  if (focusedOffer) {
    return {
      price: focusedOffer.averagePricePerDay ?? null,
      isPriceFrom: false,
      isViewed: focusedOffer.isViewed,
      isFavorite: focusedOffer.isFavorite,
      icons: focusedOffer.icon ? [focusedOffer.icon] : [],
      counterLabel,
    };
  }

  const isFavorite = offers.some(offer => offer.isFavorite);

  return {
    price: getMinPrice(offers),
    // «от» и каунтер берутся из одного источника — они не могут разойтись
    isPriceFrom: Boolean(counterLabel),
    isViewed: offers.some(offer => offer.isViewed),
    isFavorite,
    // Контроль эксперимента не меняем: у одиночного пина избранное прячет иконки объявления,
    // как в мастере. У мультипина по макету показываются все иконки вместе с избранным.
    icons: !counterLabel && isFavorite ? [] : getUniqueIcons(offers),
    counterLabel,
  };
}
